import React, { useEffect, useState } from 'react';
import { CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { CTABanner } from './CTABanner';
export interface FAQ {
  question: string;
  answer: string;
}
export interface BeforeAfterImage {
  before: string;
  after: string;
  label: string;
}
export interface ServicePageTemplateProps {
  serviceName: string;
  heroImage: string;
  heroDescription: string;
  description: string;
  benefits: string[];
  whySoftWash: string;
  faqs: FAQ[];
  beforeAfterImages: BeforeAfterImage[];
}
export function ServicePageTemplate({
  serviceName,
  heroImage,
  heroDescription,
  description,
  benefits,
  whySoftWash,
  faqs,
  beforeAfterImages
}: ServicePageTemplateProps) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="w-full" data-id="element-159" data-mp="1jsrsh8uiiw41">
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-id="element-160" data-mp="1jrzyg2gkd6fv">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: `url("${heroImage}")`
      }} data-id="element-161" data-mp="98h6cjrdpdg7"></div>
        <div className="absolute inset-0 bg-navy/80 z-10" data-id="element-162" data-mp="iyk04m6bqvfl"></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-163" data-mp="nxwtzqvm52ro">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-black text-white mb-6" data-id="element-164" data-mp="nzr4eb99dchi">
            {serviceName}
          </h1>
          <p className="text-xl text-cream max-w-3xl mx-auto" data-id="element-165" data-mp="3rfogwquw6wj0">
            {heroDescription}
          </p>
        </div>
      </section>

      {/* Service Description */}
      <section className="py-16 md:py-24 bg-white" data-id="element-166" data-mp="575ee7l18g4u">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-167" data-mp="58zzblwb088x">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-168" data-mp="1boaduu41nvb6">
            <div data-id="element-169" data-mp="1gmbxph9anynp">
              <h2 className="text-3xl font-montserrat font-bold text-navy mb-6" data-id="element-170" data-mp="1glsnyntssgkr">
                Professional {serviceName}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6" data-id="element-171" data-mp="3cnvul7a3avzv">
                {description}
              </p>
              <div className="bg-cream/30 p-6 rounded-xl border border-cream" data-id="element-172" data-mp="3lqerzbv3wgeq">
                <h3 className="text-xl font-montserrat font-bold text-navy mb-3" data-id="element-173" data-mp="3loaq0zgrprh3">
                  Why Choose Soft Washing?
                </h3>
                <p className="text-gray-700" data-id="element-174" data-mp="2typdfooy2cg6">{whySoftWash}</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-[400px]" data-id="element-175" data-mp="2p0nvj7398j6a">
              <img src={heroImage} alt={serviceName} className="w-full h-full object-cover" data-id="element-176" data-mp="32rj22cqa6o6p" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50" data-id="element-177" data-mp="2aa6on87up120">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-178" data-mp="2a8vjb3yrbl8s">
          <div className="text-center mb-12" data-id="element-179" data-mp="2694wfbccpp9j">
            <h2 className="text-3xl font-montserrat font-bold text-navy mb-4" data-id="element-180" data-mp="1w5zl4k055jxw">
              Benefits of Our Service
            </h2>
            <div className="w-24 h-1 bg-light-blue mx-auto rounded-full" data-id="element-181" data-mp="20xhkj3mincgr"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-182" data-mp="1m8l5gmm1kz6n">
            {benefits.map((benefit, index) => <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-183" data-mp="1r6weiv4ij5nt" data-mpi={index}>
                <CheckCircle className="w-6 h-6 text-light-blue shrink-0 mr-3 mt-0.5" data-id="element-184" data-mp="2ju93td225rkt" />
                <span className="text-gray-700 font-medium" data-id="element-185" data-mp="24yt3wresr1gi">{benefit}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Before & After */}
      {beforeAfterImages.length > 0 && <section className="py-16 md:py-24 bg-white" data-id="element-186" data-mp="ti30th5o6pr4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-187" data-mp="yhpji649oy7b">
            <div className="text-center mb-12" data-id="element-188" data-mp="yfuwmm9fqw0l">
              <h2 className="text-3xl font-montserrat font-bold text-navy mb-4" data-id="element-189" data-mp="1epc3emckxixy">
                See the Difference
              </h2>
              <p className="text-gray-600" data-id="element-190" data-mp="19x2jbmy5bd3l">
                Drag the slider to compare before and after our{' '}
                {serviceName.toLowerCase()}.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-191" data-mp="101r4l4mqm5vu">
              {beforeAfterImages.map((img, index) => <BeforeAfterSlider key={index} beforeImage={img.before} afterImage={img.after} label={img.label} data-id="element-192" data-mp="1xllse4p0rtwq" data-mpi={index} />)}
            </div>
          </div>
        </section>}

      {/* FAQ */}
      {faqs.length > 0 && <section className="py-16 bg-gray-50" data-id="element-193" data-mp="cadd10weuebx">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-194" data-mp="cc6i4brdkqht">
            <div className="text-center mb-12" data-id="element-195" data-mp="7b90c0aa1ygu">
              <h2 className="text-3xl font-montserrat font-bold text-navy mb-4" data-id="element-196" data-mp="7dmxceoipkr1">
                Frequently Asked Questions
              </h2>
              <div className="w-24 h-1 bg-light-blue mx-auto rounded-full" data-id="element-197" data-mp="2nxfbjx1j96zq"></div>
            </div>
            <div className="space-y-4" data-id="element-198" data-mp="2swjq73787wxg">
              {faqs.map((faq, index) => <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-199" data-mp="2xni6w3w4mx18" data-mpi={index}>
                  <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none" onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} data-id="element-200" data-mp="32oxplpau0rgg">
                    <span className="font-montserrat font-semibold text-navy pr-4" data-id="element-201" data-mp="3glup0f50nh8y">
                      {faq.question}
                    </span>
                    {openFaqIndex === index ? <ChevronUp className="w-5 h-5 text-light-blue shrink-0" data-id="element-202" data-mp="1m8v5wc1fg4wx" /> : <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" data-id="element-203" data-mp="1e9zz8vq1w2v3" />}
                  </button>
                  {openFaqIndex === index && <div className="px-6 pb-4 text-gray-600 border-t border-gray-100 pt-4" data-id="element-204" data-mp="1o7o5aht3wwsk">
                      {faq.answer}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>}

      {/* CTA */}
      <CTABanner title={`Schedule Your ${serviceName} Today`} subtitle="Contact us for a free, no-obligation quote and let us restore your property's beauty." data-id="element-205" data-mp="1t44vdhjv9l8a" />
    </div>;
}